import { Box, IconButton, Typography, makeStyles } from '@material-ui/core'
import { Dispatch, FC, SetStateAction } from 'react'
//@ts-ignore
import { IJobFilter, IJobProps } from 'common/types'
import { StyledDrawer } from 'pages/administration/components/member-management/reported-user-management/components/styled'
import { useTheme } from 'styled-components'
import { useAuth } from 'pages/auth/hooks/useAuth'
import { useAnalytics } from 'hooks/use-analytics/useAnalytics'
import { Button } from 'common/atoms/Button/Button'
import { Select } from 'common/molecules/Select/Select'
import { industries } from 'common/select-options/industries'
import { expertise } from 'common/select-options/expertise'
import { useScreenSizes } from 'hooks/use-screen-sizes/useScreenSizes'
import { CustomDivider } from 'common/atoms/Divider/CustomDivider'
import { StyledFilterDrawerContentWrapper, StyledCloseIcon, StyledDrawerInputsContainer, StyledInputContainer } from './styles'
import {
  jobTypeList,
  workplaceList,
  datePosted,
} from 'pages/content/components/jobs/components/template/JobFormMobile/data'

interface IFilterProps {
  isDrawerOpen: boolean
  setIsDrawerOpen: Dispatch<SetStateAction<boolean>>
  setFilter: Dispatch<SetStateAction<IJobFilter>>
  filter: IJobFilter
}

const initialState: IJobFilter = {
  [IJobProps.INDUSTRY]: '',
  [IJobProps.AREA_OF_EXPERTISE]: '',
  [IJobProps.WORKPLACE]: '',
  [IJobProps.DATE_POSTED]: '',
  [IJobProps.JOB_TYPE]: ''
}

const webInitialState: IJobFilter = {
  [IJobProps.INDUSTRY]: '',
  [IJobProps.AREA_OF_EXPERTISE]: ''
}


export const JobsFilterDrawer: FC<IFilterProps> = ({ isDrawerOpen, setIsDrawerOpen, filter, setFilter }) => {
  const theme = useTheme()
  const { backendUser, backendPartner } = useAuth()
  const { logEventAnalytics } = useAnalytics()
  const { isDevice } = useScreenSizes()

  const useStyles = makeStyles(() => ({
    select: {
      width: '316px',
      minWidth: '0px !important'
    }
  }))
  const classes = useStyles()

  const onChange = evt => {
    setFilter(prev => {
      const payload = {
        ...prev,
        [evt.target.name]: !!evt?.target?.value ? encodeURIComponent(evt?.target?.value) : undefined
      }

      if (evt.target.name === IJobProps.COUNTRY) {
        payload[IJobProps.PROVINCES] = ''
        payload[IJobProps.CITY] = ''
      }
      if (evt.target.name === IJobProps.PROVINCES) {
        payload[IJobProps.CITY] = ''
      }

      return payload
    })
  }

  const updateAndClose = (reset = false) => {
    setFilter(prev => {
      return reset ? {} : filter
    })

    const partnerLog = {}
    const userLog = {}

    if (backendUser?.partnerId) {
      partnerLog['Partner ID'] = backendUser?.partnerId
      partnerLog['Company Name'] = backendPartner?.name
    }

    logEventAnalytics(
      backendUser?.partnerId ? 'Partner' : backendUser?.role,
      'Job Filter Search',
      'mixpanel',
      'Job Filter Search',
      {
        'User ID': backendUser?.id,
        'Selected search company': filter?.companyName,
        'Selected search job type': filter?.jobType,
        'Selected search job title': filter?.jobTitle,
        'Selected search job location': filter?.city,
        'Selected search job workplace': filter?.workplace,
        'Selected search job industry': filter?.industry,
        'Selected search job area of expertise': filter?.areaOfExpertise,
        'Account Type': backendUser?.partnerId ? 'Partner' : 'Member',
        ...userLog,
        ...partnerLog
      },
      backendUser?.id,
      {
        $name: `${backendUser?.firstName} ${backendUser?.lastName}`,
        $email: backendUser?.username
      }
    )

    setIsDrawerOpen(false)
  }

  return (
    <StyledDrawer
      anchor="right"
      open={isDrawerOpen}
      onOpen={() => setIsDrawerOpen(true)}
      onClose={() => setIsDrawerOpen(false)}
      title="Filter"
    >
      <StyledFilterDrawerContentWrapper>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          height={isDevice ? '59px' : '44px'}
          color={theme.colors.accent1}
        >
          <Typography variant={isDevice ? 'subtitle1' : 'h4'}>Filter</Typography>
          <IconButton onClick={() => setIsDrawerOpen(false)}>
            <StyledCloseIcon />
          </IconButton>
        </Box>
      </StyledFilterDrawerContentWrapper>
      <CustomDivider color={theme.colors.supplementary1} thickness="1px" borderType="solid" />
      <StyledFilterDrawerContentWrapper>
        <StyledDrawerInputsContainer>
          {isDevice && (
            <>
              <StyledInputContainer>
                  <Select
                    width="100%"
                    name="datePosted"
                    label={'Date Posted'}
                    theme={theme}
                    menuItemList={[{label: '', value: ''}, ...datePosted]}
                    value={filter?.datePosted ? decodeURIComponent(filter?.datePosted) : ''}
                    onChange={onChange}
                    multiple={false}
                    native={false}
                    autoWidth={false}
                    MenuProps={{ classes: { paper: classes.select } }}
                    renderValue={value => (value ? value : <Box color={theme.colors.accent}>Select Date Posted</Box>)}
                  />
                </StyledInputContainer>
                <StyledInputContainer>
                  <Select
                    width="100%"
                    name="jobType"
                    label={'Job Type'}
                    theme={theme}
                    menuItemList={[{label: '', value: ''}, ...jobTypeList]}
                    value={filter?.jobType ? decodeURIComponent(filter?.jobType) : ''}
                    onChange={onChange}
                    multiple={false}
                    native={false}
                    autoWidth={false}
                    MenuProps={{ classes: { paper: classes.select } }}
                    renderValue={value => (value ? value : <Box color={theme.colors.accent}>Select Job Type</Box>)}
                  />
                </StyledInputContainer>
                <StyledInputContainer>
                  <Select
                    width="100%"
                    name="workplace"
                    label={'Workplace'}
                    theme={theme}
                    menuItemList={[{label: '', value: ''}, ...workplaceList]}
                    value={filter?.workplace ? decodeURIComponent(filter?.workplace) : ''}
                    onChange={onChange}
                    multiple={false}
                    native={false}
                    autoWidth={false}
                    MenuProps={{ classes: { paper: classes.select } }}
                    renderValue={value => (value ? value : <Box color={theme.colors.accent}>Select Workplace</Box>)}
                  />
                </StyledInputContainer>
            </>
          )}
          <StyledInputContainer>
            <Select
              width="100%"
              name="industry"
              label={'Industry'}
              theme={theme}
              menuItemList={[...industries]}
              value={filter?.industry ? decodeURIComponent(filter?.industry) : ''}
              onChange={onChange}
              multiple={false}
              native={false}
              autoWidth={false}
              MenuProps={{ classes: { paper: classes.select } }}
              renderValue={value => (value ? value : <Box color={theme.colors.accent}>Select Industry</Box>)}
            />
          </StyledInputContainer>
          <StyledInputContainer>
            <Select
              width="100%"
              name="areaOfExpertise"
              label={'Area of expertise'}
              theme={theme}
              menuItemList={[...expertise]}
              value={filter?.areaOfExpertise ? decodeURIComponent(filter?.areaOfExpertise) : ''}
              onChange={onChange}
              multiple={false}
              native={false}
              autoWidth={false}
              MenuProps={{ classes: { paper: classes.select } }}
              renderValue={value => (value ? value : <Box color={theme.colors.accent}>Select Area of Expertise</Box>)}
            />
          </StyledInputContainer>
        </StyledDrawerInputsContainer>
        <Box
          display="flex"
          flexDirection={isDevice ? 'column' : 'row'}
          justifyContent={isDevice ? 'unset' : 'center'}
          alignItems={isDevice ? 'unset' : 'center'}
          gridGap={isDevice ? 'unset' : '1rem'}
        >
          <Box width={isDevice ? '100%' : '70%'} order={isDevice ? 1 : 2}>
            <Button
              design="primary"
              fullWidth
              disabled={false}
              forwardArrowIcon
              small
              onClick={() => {
                updateAndClose()
              }}
            >
              Show Results
            </Button>
          </Box>
          <Box alignSelf={isDevice ? 'flex-end' : 'unset'} width={isDevice ? 'unset' : '30%'} order={isDevice ? 2 : 1}>
            <Button
              design="tertiary"
              disabled={false}
              small={!isDevice}
              onClick={() => {
                setFilter(isDevice ? initialState : webInitialState)
                updateAndClose(true)
              }}
            >
              Reset
            </Button>
          </Box>
        </Box>
      </StyledFilterDrawerContentWrapper>
    </StyledDrawer>
  )
}
